import React, { useMemo } from "react";
import { useField } from "formik";
import { Search } from "lucide-react";
import CollapsibleCard from "../ui/CollapsibleCard";
import Field from "../ui/Field";
import { SEO_FIELDS, SEO_LOCALES, autoSeoText, seoFieldKey } from "../seoFields";

/**
 * The SEO block: five fields, each in English and Arabic side by side.
 *
 * Every field is optional and normally left blank — the storefront generates
 * that tag from the product's own name, description and brand, in the
 * language of the page being shown. A value typed here overrides the
 * generated one for that language alone, so an English title never reaches
 * the Arabic page.
 *
 * An empty box shows, greyed-out, the exact text the storefront generates for
 * it (`autoSeo`, from the saved product). It is a placeholder, never a value:
 * nothing is stored, so it keeps following edits to the product.
 *
 * The field list lives in ../seoFields.js, which also defines the form's SEO
 * shape. Rendering from the same list is what stops a box existing here whose
 * value has nowhere to go on save.
 */

const LOCALE_META = {
  en: { suffix: "", inputClass: "pf-input", textAreaClass: "pf-textarea" },
  ar: {
    suffix: " (Arabic)",
    inputClass: "pf-input pf-input--rtl",
    textAreaClass: "pf-textarea pf-textarea--rtl",
    lang: "ar",
    dir: "rtl",
  },
};

// Shown under an empty box that is displaying generated text, so it reads as
// "this is handled", not as a field somebody forgot.
const AUTO_HINT = "Automatic — updates when the product changes";

/**
 * Advisory length note. Google truncates rather than rejects, so this never
 * blocks a save — it only tells the writer what will actually be seen. An
 * empty box shows AUTO_HINT instead when it has generated text behind it.
 */

const useLengthHint = (value, limit, isAutomatic) =>
  useMemo(() => {
    const length = typeof value === "string" ? value.trim().length : 0;
    if (length === 0) return isAutomatic ? AUTO_HINT : null;
    if (length > limit) {
      return `${length} / ${limit} — Google will cut this short`;
    }
    return `${length} / ${limit}`;
  }, [value, limit, isAutomatic]);

const SeoField = ({
  name,
  label,
  placeholder,
  autoText,
  control,
  hintLength,
  locale,
}) => {
  const [field, meta] = useField(name);
  const { inputClass, textAreaClass, lang, dir } = LOCALE_META[locale];
  const hint = useLengthHint(field.value, hintLength, Boolean(autoText));
  const error = meta.touched ? meta.error : null;

  // Formik hands back `undefined` for a key that never made it into the form
  // shape; an input flipping between undefined and a string is React's
  // uncontrolled-to-controlled warning, and it drops the first keystroke.
  const value = field.value ?? "";

  const shared = {
    id: name,
    name: field.name,
    value,
    onChange: field.onChange,
    onBlur: field.onBlur,
    // The real generated text when we have it; the generic hint otherwise
    // (Add, or a server that does not send it).
    placeholder: autoText || placeholder,
    lang,
    dir,
    "aria-invalid": !!error,
  };

  return (
    <Field label={label} htmlFor={name} hint={hint} error={error}>
      {control === "textarea" ? (
        <textarea {...shared} rows={3} className={textAreaClass} />
      ) : (
        <input {...shared} type="text" className={inputClass} />
      )}
    </Field>
  );
};

const MemoSeoField = React.memo(SeoField);

const SeoSection = ({ autoSeo }) => (
  <CollapsibleCard
    icon={<Search size={16} />}
    title="SEO"
    subtitle="Grey text is what Google sees now, written automatically from the product. Type only to override it."
    id="seo"
  >
    <div className="pf-grid pf-grid--2">
      {SEO_FIELDS.map(({ field, label, control, hintLength, ...copy }) =>
        SEO_LOCALES.map((locale) => (
          <MemoSeoField
            key={seoFieldKey(field, locale)}
            name={`seo.${seoFieldKey(field, locale)}`}
            label={`${label}${LOCALE_META[locale].suffix}`}
            placeholder={copy[locale]?.placeholder}
            autoText={autoSeoText(autoSeo, seoFieldKey(field, locale))}
            control={control}
            hintLength={hintLength}
            locale={locale}
          />
        )),
      )}
    </div>
  </CollapsibleCard>
);

export default React.memo(SeoSection);
